import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ErrorHandler, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { CookieService } from '../shared/cookies/cookie.service';
import { ContentService } from './content.service';
import { AppContent } from './models/app-content';
import { RouteTitleService } from './route-title.service';
import { SettingsService } from './settings.service';
import ShortUniqueId from 'short-unique-id';
import { AnalyticsEvent, AnalyticsEventNames } from './models/analytics-event';
import { EventsQueue } from './analytics/events-queue';
import { URLUtils } from './url-utils';
import { mergeMap, take } from 'rxjs/operators';
import { LocalStorageService } from './localStorage.service';
import { DeviceDetectionService } from './device-detection.service';
import { UserMachineIdService } from './user-machine-id.service';
import { DateUtils } from './date-utils';

declare let gtag;

class UserSession {
  id: string;
  tagId: string;
  dateCreated: Date;
  dateUpdated: Date;
}

const MAX_SESSION_TIME_IN_MS = 30 * 60 * 1000; // 30 minutes
const LOCAL_STORAGE_SESSION_KEY = 'USER_ANALYTICS_SESSION';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  trackingId = environment.GA_TRACKING_CODE;
  canTrack = true;

  private isGALoaded = false;

  private session: UserSession;
  private sessionUidGenerator: ShortUniqueId;
  private screenRes: string;

  private currentPage: {
    name: string;
    url: string;
  } = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: string,
    private settings: SettingsService,
    private router: Router,
    private contentService: ContentService<AppContent>,
    private routeTitleService: RouteTitleService,
    private cookieService: CookieService,
    private eventsQueue: EventsQueue,
    private errorHandler: ErrorHandler,
    private localStorageService: LocalStorageService,
    private deviceDetectionService: DeviceDetectionService,
    private userMachineIdService: UserMachineIdService
  ) {
    this.sessionUidGenerator = new ShortUniqueId({ length: 10 });
    this.session = this.readSession();
    this.screenRes = `${window.screen.width}x${window.screen.height}`;
  }

  /**
   * Initialise the service by 1) fetching the trackingID from the server and
   * 2) injecting the GA JS snippet in the page.
   */
  init() {
    if (!this.settings.settings.preview && isPlatformBrowser(this.platformId)) {
      this.contentService.appContent$
        .pipe(
          take(1),
          mergeMap((appContent) => {
            if (appContent && appContent.gaTrackingCode) {
              this.trackingId = appContent.gaTrackingCode;
            }
            return this.cookieService.hasAgreedToGa$;
          })
        )
        .subscribe((hasAgreed) => {
          this.canTrack = hasAgreed && this.deviceDetectionService.isMobile();

          if (hasAgreed && !this.isGALoaded) {
            this.loadGoogleAnalytics();
            this.setupRouteTracking();
            this.setupUnloadHandler();
            this.isGALoaded = true;
          }
        });
    }
  }

  /**
   * When the user navigates away from the apps, we need to send an exit event to our servers
   * to track that the current page_view is finished,
   * and clear the queue of events to make sure that all pending events are sent.
   */
  private setupUnloadHandler() {
    window.addEventListener('beforeunload', () => {
      // The app_exit event is the only one that should check that the user is identified before sending the event.
      // All other events should not even be logged in case the user is not identified, because the app.module logic
      // should have redirected the user to the TAP URL.
      if (
        this.currentPage &&
        this.userMachineIdService.hasMachineId() &&
        this.userMachineIdService.isMachineIdSynced()
      ) {
        this._sendEvent(AnalyticsEventNames.APP_EXIT);
      }
      this.eventsQueue.clearQueue();
    });
  }

  /**
   * Inject the Javascript snippet in the HTML page that loads the GA library.
   */
  private loadGoogleAnalytics(): void {
    let gaScript = this.document.createElement('script');
    gaScript.setAttribute('async', 'true');
    gaScript.setAttribute(
      'src',
      `https://www.googletagmanager.com/gtag/js?id=${this.trackingId}`
    );

    let gaScript2 = this.document.createElement('script');
    gaScript2.innerText = `window.dataLayer = window.dataLayer || []; function gtag(){window.dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${this.trackingId}', {send_page_view: false});`;

    const docHead = this.document.getElementsByTagName('head')[0];
    docHead.appendChild(gaScript);
    docHead.appendChild(gaScript2);
  }

  /**
   * Setup a route events subscriber that manually sends a page_view event to Google Analytics,
   * whenever a new route is loaded.
   */
  private setupRouteTracking() {
    // for Google Analytics
    if (!this.settings.settings.preview) {
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          let pageTitle = this.routeTitleService.getPageTitle();
          // if the route does not have a page title, it reverts to manual (the component needs to send the event)
          if (pageTitle) {
            this.trackPageView(event.urlAfterRedirects, pageTitle);
          }
        }
      });
      // adding machine Id as user property to GA4
      gtag('config', this.trackingId, {
        user_id: this.settings.settings.machineId,
      });
      gtag('set', 'user_properties', {
        machine_id: this.settings.settings.machineId,
      });
    }
  }

  trackCustomEvent(eventName: string, params: object = {}) {
    this.sendEvent(eventName, {
      ...params,
      campaignId: this.settings.settings.campaignId,
      languageCode: this.settings.settings.experienceLang,
      experienceType: this.settings.settings.experienceType,
      brandId: this.settings.settings.brandId,
      experienceId: this.settings.settings.experienceId,
      tagId: this.settings.settings.tagId,
      rollId: this.settings.settings.rollId,
    });
  }

  trackPageView(pageUrl: string, pageTitle: string, params?: object) {
    this.sendEvent('page_view', {
      ...params,
      page_title: pageTitle,
      page_location: pageUrl,
      page_path: pageUrl,
      campaignId: this.settings.settings.campaignId,
      languageCode: this.settings.settings.experienceLang,
      experienceType: this.settings.settings.experienceType,
      brandId: this.settings.settings.brandId,
      experienceId: this.settings.settings.experienceId,
      tagId: this.settings.settings.tagId,
      rollId: this.settings.settings.rollId,
    });
  }

  private sendEvent(eventName: string, params: any) {
    if (!this.settings.settings.preview && this.canTrack) {
      gtag('event', eventName, params);

      // send event to proq servers
      this._sendEvent(eventName, params);
    }
  }

  private _sendEvent(eventName: string, params?: any) {
    try {
      const sessionId = this.getSessionId();

      const currentDate = new Date();

      const event = new AnalyticsEvent({
        sessionId: sessionId,
        campaignId: this.settings.settings.campaignId,
        rollId: this.settings.settings.rollId,
        tagId: this.settings.settings.tagId,
        languageCode: this.settings.settings.experienceLang,
        eventName: eventName as AnalyticsEventNames,
        eventValue: params?.page_title || this.currentPage?.name,
        pageTitle: params?.page_title || this.currentPage?.name,
        pageUrl: params?.page_location || this.currentPage?.url,
        engagementTimeMs: 100, // need to fix this
        experienceId: this.settings.settings.experienceId,
        experienceType: this.settings.settings.experienceType,
        screenRes: this.screenRes,
        brandId: +this.settings.settings.brandId,
        timestamp: currentDate.getTime(),
        datetime: currentDate.toISOString(),
        timezone: DateUtils.getTimezone(),
      });

      // Convert previous custom events in the different format we use in self-hosted events table.
      if (eventName === 'click_event') {
        event.eventName = AnalyticsEventNames.CLICK;
        event.eventValue = params.actionName; // Name of button clicked
      } else if (eventName === 'action_Card') {
        event.eventName = AnalyticsEventNames.LINK;
        event.eventValue = params.actionSlug; // The URL that the action card redirects to
      } else if (eventName === 'share_event') {
        event.eventName = AnalyticsEventNames.SHARE;
        event.eventValue = params.socialPlatform || params.shareTitle;
      } else if (eventName === 'page_view') {
        /**
         * The URL received from route change events contain all query parameters that we do not need to log in analytics.
         * These are things like campaignId, tagId, rollId, etc.
         * Before logging this URL, we strip the URL of those extra query parameter so it's cleanly logged in analytics.
         */
        event.pageUrl = URLUtils.removeQueryParameters(event.pageUrl, [
          'campaignId',
          'tagId',
          'rollId',
          'machineId',
          'viewMode',
        ]);

        // Saves it into an internal variable to be reused by other events (e.g. clicks etc) that do not have url and title available in the payload.
        this.currentPage = {
          name: event.pageTitle,
          url: event.pageUrl,
        };
      }

      this.eventsQueue.addEvent(event);

      // Update the last updated date of session
      this.session.dateUpdated = new Date();
      this.saveSession();
    } catch (err) {
      // Delegates handling the error to the global error handler that sends the monitoring reports
      // But it does not block execution by throwing an error
      this.errorHandler.handleError(err);
    }
  }

  private getSessionId() {
    if (
      !this.session ||
      this.isSessionExpired() ||
      this.session.tagId !== this.settings.settings.tagId
    ) {
      console.debug(`Session not found or expired. Creating a new one.`);
      // Create new session and store it in local storage
      this.session = {
        id: this.sessionUidGenerator(),
        tagId: this.settings.settings.tagId,
        dateCreated: new Date(),
        dateUpdated: new Date(),
      };
      console.debug(`Generated session ID = ${this.session.id}`);
      this.saveSession();
    }
    return this.session.id;
  }

  private readSession() {
    let session: UserSession;

    // Retrieves the session from local storage. If the session is not found or an error occurs, sessionString will be set to null.
    const sessionString = this.localStorageService.getItem(
      LOCAL_STORAGE_SESSION_KEY
    );

    if (sessionString) {
      const sessionObj = JSON.parse(sessionString);
      session = {
        id: sessionObj.id,
        tagId: this.settings.settings.tagId,
        dateCreated: new Date(sessionObj.dateCreated),
        dateUpdated: new Date(sessionObj.dateUpdated),
      };
    }

    return session;
  }

  private saveSession() {
    return this.localStorageService.setItem(
      LOCAL_STORAGE_SESSION_KEY,
      JSON.stringify(this.session)
    );
  }

  private isSessionExpired() {
    const currentTimestamp = new Date().getTime();
    return (
      this.session &&
      currentTimestamp - this.session.dateUpdated.getTime() >=
        MAX_SESSION_TIME_IN_MS
    );
  }
}
